(() => {
  'use strict';
  if (customElements.get('product-orientation')) return;

  class ProductOrientation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.owner = this.closest('[data-section-type="product"]');
      if (!this.owner) return;
      this.controller = new AbortController();
      this.native = this.dataset.defaultOrientation || 'Portrait';
      this.choice = this.choice || (this.native === 'Landscape' ? 'Landscape' : 'Portrait');
      this.picker = this.closest('variant-radios, variant-selects');
      this.variantSizes = new Map();
      try {
        const variants = JSON.parse(this.picker?.querySelector('script[type="application/json"]')?.textContent || '[]');
        variants.forEach(variant => this.variantSizes.set(String(variant.id), variant.options?.[Number(this.dataset.sizeOptionIndex)]));
      } catch (_) { /* The selected size control remains the fallback. */ }
      this.addEventListener('click', event => {
        const card = event.target.closest('[data-orientation-value]');
        if (card && this.contains(card)) this.select(card.dataset.orientationValue);
      }, { signal: this.controller.signal });
      this.owner.addEventListener('change', event => this.refresh(event.target), { signal: this.controller.signal });
      // Capture runs before the product form serializes its cart request.
      this.owner.addEventListener('submit', event => this.refresh(event.target), {
        capture: true, signal: this.controller.signal
      });
      // Variant/theme-editor rendering may replace either owning form.
      this.observer = new MutationObserver(() => this.refresh());
      this.observer.observe(this.owner, { childList: true, subtree: true });
      this.refresh();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }

    forms() {
      const ids = [this.dataset.formId, this.dataset.stickyFormId];
      // input[name=id] shadows form.id through named form access.
      return Array.from(this.owner.querySelectorAll('form')).filter(form => ids.includes(form.getAttribute('id')));
    }

    size(source) {
      const forms = this.forms();
      const sourceForm = source?.closest('form');
      const fromOwnedForm = forms.includes(sourceForm);
      const stickySelect = source?.getAttribute('id') === this.dataset.stickyVariantId;
      const variantId = stickySelect ? source.value :
        (fromOwnedForm ? sourceForm : forms[0])?.querySelector('[name="id"]')?.value;
      return this.variantSizes.get(String(variantId)) ||
        this.picker?.querySelector('fieldset[data-option-handle="size"] input:checked')?.value ||
        this.dataset.selectedSize || '';
    }

    select(value) {
      if (value !== 'Portrait' && value !== 'Landscape') return;
      if (this.squareCanvas || (this.dataset.nativeOnly === 'true' && value !== this.native)) return;
      this.choice = value;
      this.refresh();
    }

    text(selector, value) {
      const node = this.querySelector(selector);
      // Avoid generating a new child-list mutation when nothing changed.
      if (node && node.textContent !== value) node.textContent = value;
    }

    refresh(source) {
      const dimensions = this.size(source).match(/^\s*(\d+(?:\.\d+)?)\s*(?:cm|in(?:ches)?|["″])?\s*[x×]\s*(\d+(?:\.\d+)?)/i);
      this.squareCanvas = !!dimensions && Number(dimensions[1]) > 0 && Number(dimensions[1]) === Number(dimensions[2]);
      const nativeOnly = this.dataset.nativeOnly === 'true';
      const squareArt = nativeOnly && this.native === 'Square' && !this.squareCanvas;
      this.value = this.squareCanvas ? 'Square' : squareArt ? null : nativeOnly ? this.native : this.choice;
      this.text('[data-orientation-label]', squareArt ? 'Artwork orientation:' : 'Orientation:');
      this.text('[data-orientation-header]', squareArt ? 'Square' : this.value);
      this.text('[data-orientation-fixed-title]', this.squareCanvas ? 'Square canvas' : 'Square artwork');
      this.text('[data-orientation-fixed-description]', this.squareCanvas ? 'Equal width and height' : 'The original artwork is square.');
      const fixed = this.querySelector('[data-orientation-fixed]');
      const grid = this.querySelector('[data-orientation-grid]');
      if (fixed) fixed.hidden = !this.squareCanvas && !squareArt;
      if (grid) grid.hidden = this.squareCanvas || squareArt;
      const note = this.querySelector('[data-orientation-native-note]');
      if (note) note.hidden = this.squareCanvas || squareArt;
      this.querySelectorAll('[data-orientation-value]').forEach(card => {
        const selected = card.dataset.orientationValue === this.value;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-pressed', String(selected));
        card.disabled = this.squareCanvas || (nativeOnly && card.dataset.orientationValue !== this.native);
      });
      this.syncForms();
    }

    syncForms() {
      this.forms().forEach(form => {
        let input = form.querySelector('input[name="properties[Orientation]"]');
        if (!this.value) {
          input?.remove();
          return;
        }
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[Orientation]';
          form.appendChild(input);
        }
        input.value = this.value;
      });
    }
  }

  customElements.define('product-orientation', ProductOrientation);
})();
